import { call, cancelled, put, select } from 'redux-saga/effects'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { selectPageConfigurationData } from '@/store/PageConfiguration/PageConfigurationSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import {
  addCommonFormErrors,
  deleteCommonFormErrors,
  updateOfferConfiguration,
  updateOfferModuleConfiguration,
  updateProviderConfiguration,
} from '../../../SubscriptionFormConfigurationSlice'
import { mapProviderConfigData } from './mapProviderConfigData'
import * as Sentry from '@sentry/react'

export const offerModuleActions = {
  OFFER_MODULE_LOAD_PROVIDERS: 'OFFER_MODULE_LOAD_PROVIDERS',
  OFFER_MODULE_LOAD_OFFERS: 'OFFER_MODULE_LOAD_OFFERS',
  OFFER_MODULE_LOAD_PROVIDERS_OPTIONS: 'OFFER_MODULE_LOAD_PROVIDERS_OPTIONS',
  OFFER_MODULE_LOAD_OFFER_INFORMATION: 'OFFER_MODULE_LOAD_OFFER_INFORMATION',
  OFFER_MODULE_LOAD_PROVIDER_INFORMATION: 'OFFER_MODULE_LOAD_PROVIDER_INFORMATION',
}

export function* loadProviders({ params, errorTooManyRequests }) {
  const controller = new AbortController()
  const { authTokenSession, lang } = yield select(selectAuthData)
  const { load_providers_url: url } = yield select(selectPageConfigurationData)
  const fullUrl = `${url}?lang=${lang}&${params}`
  yield put(deleteCommonFormErrors('load_provider_list'))
  yield put(updateOfferModuleConfiguration({ providerLoading: true }))

  try {
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const signal = controller.signal
    const response = yield call(fetch, fullUrl, { ...config, signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    if (!isValid) {
      yield put(updateOfferModuleConfiguration({ providerLoading: false }))
      if (import.meta.env.PROD) {
        Sentry.captureException(new Error('loadProvidersError'), { extra: { responseData, status: response.status } })
      }
      return
    }
    if (response.status === 429) {
      yield put(updateOfferModuleConfiguration({ providerLoading: false }))
      yield put(addCommonFormErrors({ load_provider_list: errorTooManyRequests }))
      return
    }
    yield put(updateOfferModuleConfiguration({ offers: {}, providers: responseData, providerLoading: false }))
  } catch (error) {
    yield put(updateOfferModuleConfiguration({ providerLoading: false }))
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('loadProvidersError'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* loadOffers({ params }) {
  const controller = new AbortController()
  const { authTokenSession, lang } = yield select(selectAuthData)
  const { load_offers_url: url } = yield select(selectPageConfigurationData)
  const fullUrl = `${url}?lang=${lang}&${params}`
  yield put(updateOfferModuleConfiguration({ offerLoading: true }))

  try {
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const signal = controller.signal
    const response = yield call(fetch, fullUrl, { ...config, signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    if (!isValid) {
      if (import.meta.env.PROD && response.status !== 422) {
        Sentry.captureException(new Error('loadOffersError'), { extra: { responseData, status: response.status } })
      }
      return
    }
    yield put(updateOfferModuleConfiguration({ offers: responseData.list, offerLoading: false }))
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('loadOffersError'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* loadProviderOptions({ providerId, productType }) {
  const controller = new AbortController()
  const { authTokenSession, lang } = yield select(selectAuthData)
  const { load_provider_options_url: url } = yield select(selectPageConfigurationData)
  const fullUrl = `${url}/${providerId}/${productType}?lang=${lang}`

  try {
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const signal = controller.signal
    const response = yield call(fetch, fullUrl, { ...config, signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    if (!isValid) {
      if (import.meta.env.PROD && response.status !== 422) {
        Sentry.captureException(new Error('loadProviderOptionsError'), {
          extra: { responseData, status: response.status },
        })
      }
      return
    }
    yield put(updateOfferModuleConfiguration({ providerOptions: responseData }))
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('loadProviderOptionsError'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* loadOfferInformation({ offerId }) {
  if (!offerId || parseInt(offerId) <= 0) {
    yield put(updateOfferConfiguration({}))
    return
  }
  const controller = new AbortController()
  const { authTokenSession, lang } = yield select(selectAuthData)
  const { load_offer_info_url: url } = yield select(selectPageConfigurationData)
  const fullUrl = `${url}?lang=${lang}&offer_id=${offerId}`
  try {
    const signal = controller.signal
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const response = yield call(fetch, fullUrl, { ...config, signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    if (!isValid) {
      if (import.meta.env.PROD && response.status !== 422) {
        Sentry.captureException(new Error('loadOfferInformationError'), {
          extra: { responseData, status: response.status },
        })
      }
      return
    }
    yield put(updateOfferConfiguration(responseData.offer))
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('loadOfferInformationError'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* loadProviderInformation({ providerId }) {
  const controller = new AbortController()
  const { authTokenSession, lang } = yield select(selectAuthData)
  const { load_organization_config_url: url } = yield select(selectPageConfigurationData)
  const fullUrl = `${url}?lang=${lang}&provider_id=${providerId}`

  try {
    const signal = controller.signal
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const response = yield call(fetch, fullUrl, { ...config, signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    if (!isValid) {
      if (import.meta.env.PROD && response.status !== 422) {
        Sentry.captureException(new Error('loadProviderInformationError'), {
          extra: { responseData, status: response.status },
        })
      }
      return
    }
    yield put(updateProviderConfiguration(mapProviderConfigData(responseData)))
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('loadProviderInformationError'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}
